import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart as BC,
  ReferenceDot,
  ResponsiveContainer,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from "recharts";
import { AxisDomain } from "recharts/types/util/types";

import { PercentString } from "shared/types";
import { capitalizeFirstLetter } from "shared/utils";

import {
  PRIMARY_CHART_COLOR,
  SECONDARY_CHART_COLOR,
} from "features/ui/charts/constants";
import { DataElement, MarginProps } from "features/ui/charts/types";

import AboveBarValueLabel from "./AboveBarValueLabel";

const AXIS_LINE_COLOR = "#E0E0E0";
const AXIS_TEXT_COLOR = "#6F6F6F";

interface SmartGroupBarChartProps {
  data: DataElement[];
  xAxisKey: string;
  yAxisKey: string;
  height?: number | PercentString;
  width?: number | PercentString;
  margin?: MarginProps;
  xAxisProps?: XAxisProps;
  yAxisProps?: YAxisProps;
  yAxisWidth?: number;
  yAxisDomain?: AxisDomain;
  maxBarWidth?: number;
  stackedBarKey?: string;
  /** Legend label for the stacked (top) series. */
  stackedBarLegendLabel?: string;
  chartTitle?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  eventsPeriod?: string;
}

const SmartGroupBarChart = memo(
  ({
    data,
    xAxisKey,
    yAxisKey,
    height = 180,
    width = "100%",
    margin = { top: 30, right: 12, bottom: 13, left: 16 },
    xAxisProps,
    yAxisProps,
    yAxisWidth = 50,
    yAxisDomain = [0, "auto"],
    maxBarWidth = 40,
    stackedBarKey,
    stackedBarLegendLabel,
    chartTitle,
    xAxisLabel,
    yAxisLabel,
    eventsPeriod,
  }: SmartGroupBarChartProps) => {
    const { t } = useTranslation("common");
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    if (!data || !Array.isArray(data) || data.length === 0) {
      return <div>{t("no_data")}</div>;
    }

    const capitalizedEventsPeriod = eventsPeriod
      ? capitalizeFirstLetter(eventsPeriod)
      : "";

    const hoveredEntry = hoveredIndex === null ? undefined : data[hoveredIndex];
    const hoveredValue = Number(hoveredEntry?.[yAxisKey] ?? 0);
    const hoveredStackedValue = stackedBarKey
      ? Number(hoveredEntry?.[stackedBarKey] ?? 0)
      : 0;

    // Legend labels based on whether bars are stacked
    const legendLabels = stackedBarKey
      ? [
          { value: capitalizedEventsPeriod, color: PRIMARY_CHART_COLOR },
          {
            value: stackedBarLegendLabel ?? t("charts.all_time"),
            color: SECONDARY_CHART_COLOR,
          },
        ]
      : [{ value: capitalizedEventsPeriod, color: PRIMARY_CHART_COLOR }];

    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-8 mb-2">
          {chartTitle && <div className="text-sm font-bold">{chartTitle}</div>}
          <div className="flex gap-2">
            {legendLabels.map((item) => (
              <div key={item.value} className="flex items-center gap-1">
                <div
                  className="w-3 h-3"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-gray-400 text-xs">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
        <ResponsiveContainer height={height} width={width}>
          <BC
            data={data}
            margin={margin}
            barGap={1}
            onMouseMove={(state) => {
              // recharts 3 reports the active index as a string
              const index =
                state.activeTooltipIndex == null
                  ? NaN
                  : Number(state.activeTooltipIndex);
              setHoveredIndex(Number.isFinite(index) ? index : null);
            }}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <Bar
              dataKey={yAxisKey}
              fill={PRIMARY_CHART_COLOR}
              isAnimationActive={false}
              maxBarSize={maxBarWidth}
              stackId={stackedBarKey ? "stack" : undefined}
            />
            {stackedBarKey && (
              <Bar
                dataKey={stackedBarKey}
                fill={SECONDARY_CHART_COLOR}
                isAnimationActive={false}
                maxBarSize={maxBarWidth}
                stackId="stack"
              />
            )}
            {/* The hover label is a ReferenceDot rather than a Bar `label`:
                recharts 3 drops zero-size bar rects before building labels, so
                Bar labels never render for zero values and their index drifts
                from activeTooltipIndex. */}
            {hoveredEntry && (
              <ReferenceDot
                x={hoveredEntry[xAxisKey]}
                y={hoveredValue + hoveredStackedValue}
                shape={({ cx, cy }) => (
                  <AboveBarValueLabel
                    cx={cx}
                    cy={cy}
                    value={hoveredValue + hoveredStackedValue}
                  />
                )}
              />
            )}
            <YAxis
              width={yAxisWidth}
              domain={yAxisDomain}
              axisLine={{ stroke: AXIS_LINE_COLOR }}
              tickLine={{ stroke: AXIS_LINE_COLOR }}
              tick={{ fill: AXIS_TEXT_COLOR }}
              label={{
                value: yAxisLabel,
                angle: -90,
                position: "insideLeft",
                offset: -10,
                style: { textAnchor: "middle", fill: AXIS_TEXT_COLOR },
              }}
              {...yAxisProps}
            />
            <XAxis
              dataKey={xAxisKey}
              axisLine={{ stroke: AXIS_LINE_COLOR }}
              tickLine={{ stroke: AXIS_LINE_COLOR }}
              tick={{ fill: AXIS_TEXT_COLOR, dy: 3 }}
              interval="preserveStartEnd"
              minTickGap={30}
              label={{
                value: xAxisLabel ?? t("charts.production_week"),
                position: "insideBottom",
                offset: -10,
                style: { fill: AXIS_TEXT_COLOR },
              }}
              {...xAxisProps}
            />
          </BC>
        </ResponsiveContainer>
      </div>
    );
  }
);

export default SmartGroupBarChart;
